// @ts-nocheck
/**
 * 借金管理システム
 * レバレッジ取引での損失超過時に発生する借金の管理
 */

import { INVESTMENT } from '../../data/constants';

// ========================================
// 定数
// ========================================

export const DEBT_CONSTANTS = {
  REPAYMENT_DEADLINE: 7,      // 返済期限（戦数）
  INTEREST_RATE: 0.05,        // 利息率（5%/戦）
  AUTO_REPAYMENT_RATE: 0.5,   // 対戦報酬からの自動返済率（50%）
  SEIZURE_RATE: 0.5,          // 差し押さえ時の充当率（50%）
  PROFIT_CAP: 10000,          // 自動対戦ループ時の利益上限
};

// ========================================
// 借金状態の初期化・管理
// ========================================

/**
 * 借金状態の初期構造を作成
 * @returns {Object} 初期借金状態
 */
export function createInitialDebtState() {
  return {
    amount: 0,              // 現在の借金総額（利息込み）
    originalAmount: 0,      // 元本
    interest: 0,            // 累計利息
    deadline: null,         // 返済期限（戦闘番号）
    createdAt: null,        // 発生日（戦闘番号）
    source: null,           // 発生源（'leverage' | 'fx' | 'other'）
    lastInterestAt: null,   // 最後に利息が加算された戦闘番号
  };
}

/**
 * 借金があるか確認
 * @param {Object} debtData - 借金データ
 * @returns {boolean} 借金があるか
 */
export function hasDebt(debtData) {
  return debtData && debtData.amount > 0;
}

/**
 * 借金の返済期限が過ぎているか確認
 * @param {Object} debtData - 借金データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {boolean} 期限切れか
 */
export function isDebtOverdue(debtData, currentBattleNumber) {
  if (!hasDebt(debtData)) return false;
  return currentBattleNumber > debtData.deadline;
}

/**
 * 返済期限までの残り戦数を取得
 * @param {Object} debtData - 借金データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {number} 残り戦数（負の場合は期限切れ）
 */
export function getRemainingDays(debtData, currentBattleNumber) {
  if (!hasDebt(debtData)) return 0;
  return debtData.deadline - currentBattleNumber;
}

// ========================================
// 借金の発生
// ========================================

/**
 * 借金を作成
 * @param {number} amount - 借金額
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {string} source - 発生源
 * @returns {Object} 新しい借金状態
 */
export function createDebt(amount, currentBattleNumber, source = 'leverage') {
  return {
    amount: Math.abs(amount),
    originalAmount: Math.abs(amount),
    interest: 0,
    deadline: currentBattleNumber + DEBT_CONSTANTS.REPAYMENT_DEADLINE,
    createdAt: currentBattleNumber,
    source,
    lastInterestAt: currentBattleNumber,
  };
}

/**
 * 既存の借金に追加
 * @param {Object} debtData - 現在の借金データ
 * @param {number} additionalAmount - 追加額
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {Object} 更新された借金状態
 */
export function addToDebt(debtData, additionalAmount, currentBattleNumber) {
  if (!hasDebt(debtData)) {
    return createDebt(additionalAmount, currentBattleNumber);
  }

  return {
    ...debtData,
    amount: debtData.amount + Math.abs(additionalAmount),
    originalAmount: debtData.originalAmount + Math.abs(additionalAmount),
    // 期限は延長しない（最初の借金発生時の期限を維持）
  };
}

// ========================================
// 利息計算
// ========================================

/**
 * 日次利息を計算・適用
 * @param {Object} debtData - 借金データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {Object} { newDebtData, interestAdded }
 */
export function applyDailyInterest(debtData, currentBattleNumber) {
  if (!hasDebt(debtData)) {
    return { newDebtData: debtData, interestAdded: 0 };
  }

  // 既に今日の利息は加算済み
  if (debtData.lastInterestAt >= currentBattleNumber) {
    return { newDebtData: debtData, interestAdded: 0 };
  }

  // 利息計算（発生日から経過した日数分 - 既に加算済みの日数）
  const daysToCharge = currentBattleNumber - debtData.lastInterestAt;
  let interestAdded = 0;

  for (let i = 0; i < daysToCharge; i++) {
    // 複利計算（毎日5%）
    interestAdded += Math.floor(debtData.amount * DEBT_CONSTANTS.INTEREST_RATE);
  }

  return {
    newDebtData: {
      ...debtData,
      amount: debtData.amount + interestAdded,
      interest: debtData.interest + interestAdded,
      lastInterestAt: currentBattleNumber,
    },
    interestAdded,
  };
}

/**
 * 予想される次回利息を計算
 * @param {Object} debtData - 借金データ
 * @returns {number} 次回加算される利息
 */
export function getNextInterest(debtData) {
  if (!hasDebt(debtData)) return 0;
  return Math.floor(debtData.amount * DEBT_CONSTANTS.INTEREST_RATE);
}

// ========================================
// 返済処理
// ========================================

/**
 * 所持金をすべて返済に使用（差し押さえ前の優先返済）
 * @param {Object} debtData - 借金データ
 * @param {number} gold - 所持金
 * @returns {Object} { newDebtData, goldUsed, remainingDebt, isFullyPaid }
 */
export function repayWithAllGold(debtData, gold) {
  if (!hasDebt(debtData) || gold <= 0) {
    return {
      newDebtData: debtData,
      goldUsed: 0,
      remainingDebt: debtData?.amount || 0,
      isFullyPaid: !hasDebt(debtData),
    };
  }

  const goldUsed = Math.min(gold, debtData.amount);
  const newAmount = debtData.amount - goldUsed;

  if (newAmount <= 0) {
    // 所持金だけで完済
    return {
      newDebtData: createInitialDebtState(),
      goldUsed,
      remainingDebt: 0,
      isFullyPaid: true,
    };
  }

  // まだ借金が残っている
  return {
    newDebtData: {
      ...debtData,
      amount: newAmount,
    },
    goldUsed,
    remainingDebt: newAmount,
    isFullyPaid: false,
  };
}

/**
 * 借金を返済（検証）
 * @param {Object} debtData - 借金データ
 * @param {number} gold - 所持金
 * @param {number} amount - 返済額
 * @returns {Object} { valid, error, remainingDebt }
 */
export function validateRepayment(debtData, gold, amount) {
  if (!hasDebt(debtData)) {
    return { valid: false, error: '借金がありません' };
  }

  if (amount <= 0) {
    return { valid: false, error: '返済額は正の値である必要があります' };
  }

  if (amount > gold) {
    return { valid: false, error: '所持金が不足しています' };
  }

  const remainingDebt = Math.max(0, debtData.amount - amount);

  return {
    valid: true,
    error: null,
    remainingDebt,
    actualRepayment: Math.min(amount, debtData.amount),
  };
}

/**
 * 借金を返済
 * @param {Object} debtData - 借金データ
 * @param {number} amount - 返済額
 * @returns {Object} { newDebtData, actualRepayment }
 */
export function repayDebt(debtData, amount) {
  if (!hasDebt(debtData)) {
    return { newDebtData: debtData, actualRepayment: 0 };
  }

  const actualRepayment = Math.min(amount, debtData.amount);
  const newAmount = debtData.amount - actualRepayment;

  if (newAmount <= 0) {
    // 完済
    return {
      newDebtData: createInitialDebtState(),
      actualRepayment,
    };
  }

  return {
    newDebtData: {
      ...debtData,
      amount: newAmount,
    },
    actualRepayment,
  };
}

/**
 * 対戦報酬からの自動返済額を計算
 * @param {Object} debtData - 借金データ
 * @param {number} battleReward - 対戦報酬
 * @returns {Object} { deductionAmount, remainingReward }
 */
export function calculateAutoRepayment(debtData, battleReward) {
  if (!hasDebt(debtData)) {
    return { deductionAmount: 0, remainingReward: battleReward };
  }

  const deductionAmount = Math.min(
    Math.floor(battleReward * DEBT_CONSTANTS.AUTO_REPAYMENT_RATE),
    debtData.amount
  );

  return {
    deductionAmount,
    remainingReward: battleReward - deductionAmount,
  };
}

/**
 * 対戦報酬から自動返済を適用
 * @param {Object} debtData - 借金データ
 * @param {number} battleReward - 対戦報酬
 * @returns {Object} { newDebtData, deductionAmount, remainingReward }
 */
export function applyAutoRepayment(debtData, battleReward) {
  const { deductionAmount, remainingReward } = calculateAutoRepayment(debtData, battleReward);
  const { newDebtData, actualRepayment } = repayDebt(debtData, deductionAmount);

  return {
    newDebtData,
    deductionAmount: actualRepayment,
    remainingReward,
  };
}

// ========================================
// 制限チェック
// ========================================

/**
 * 借金中の制限をチェック
 * @param {Object} debtData - 借金データ
 * @param {string} action - アクション種別
 * @returns {Object} { allowed, reason }
 */
export function checkDebtRestriction(debtData, action) {
  if (!hasDebt(debtData)) {
    return { allowed: true, reason: null };
  }

  const restrictedActions = {
    new_investment: '借金返済中は新規投資ができません',
    pack_purchase: '借金返済中はパック購入ができません',
    tournament_bet: '借金返済中は大会への賭けができません',
    card_buy: '借金返済中はカード購入ができません',
  };

  if (restrictedActions[action]) {
    return { allowed: false, reason: restrictedActions[action] };
  }

  // 許可されているアクション
  const allowedActions = ['battle', 'card_sell', 'debt_repay'];
  if (allowedActions.includes(action)) {
    return { allowed: true, reason: null };
  }

  return { allowed: true, reason: null };
}

// ========================================
// UI用ヘルパー
// ========================================

/**
 * 借金状態のサマリーを取得
 * @param {Object} debtData - 借金データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {Object} サマリー情報
 */
export function getDebtSummary(debtData, currentBattleNumber) {
  if (!hasDebt(debtData)) {
    return {
      hasDebt: false,
      amount: 0,
      originalAmount: 0,
      interest: 0,
      remainingDays: 0,
      isOverdue: false,
      nextInterest: 0,
    };
  }

  return {
    hasDebt: true,
    amount: debtData.amount,
    originalAmount: debtData.originalAmount,
    interest: debtData.interest,
    remainingDays: getRemainingDays(debtData, currentBattleNumber),
    isOverdue: isDebtOverdue(debtData, currentBattleNumber),
    nextInterest: getNextInterest(debtData),
    deadline: debtData.deadline,
    createdAt: debtData.createdAt,
    source: debtData.source,
  };
}

/**
 * 差し押さえ予定リストを生成
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Object} debtData - 借金データ
 * @param {Function} getCardValue - カード価値計算関数
 * @returns {Array} 差し押さえ予定カードリスト
 */
export function getSeizurePreview(collection, debtData, getCardValue) {
  if (!hasDebt(debtData)) return [];

  // レアリティ順にソート（高い順）
  const rarityOrder = ['GR', 'UR', 'SR', 'R', 'UC', 'C'];
  const sortedCards = [...collection].sort((a, b) => {
    const aRarityIndex = rarityOrder.indexOf(a.rarity);
    const bRarityIndex = rarityOrder.indexOf(b.rarity);
    if (aRarityIndex !== bRarityIndex) {
      return aRarityIndex - bRarityIndex;
    }
    // 同レアリティ内は価格高い順
    return getCardValue(b) - getCardValue(a);
  });

  const seizureList = [];
  let totalValue = 0;

  for (const card of sortedCards) {
    if (totalValue >= debtData.amount) break;

    const cardValue = getCardValue(card);
    const seizureValue = Math.floor(cardValue * DEBT_CONSTANTS.SEIZURE_RATE);

    seizureList.push({
      ...card,
      cardValue,
      seizureValue,
    });
    totalValue += seizureValue;
  }

  return seizureList;
}

// ========================================
// エクスポート
// ========================================

export default {
  DEBT_CONSTANTS,
  createInitialDebtState,
  hasDebt,
  isDebtOverdue,
  getRemainingDays,
  createDebt,
  addToDebt,
  applyDailyInterest,
  getNextInterest,
  repayWithAllGold,
  validateRepayment,
  repayDebt,
  calculateAutoRepayment,
  applyAutoRepayment,
  checkDebtRestriction,
  getDebtSummary,
  getSeizurePreview,
};
